import { TenderStatus, ProposalStatus, StatusInfoName } from 'common/tenders/tenders.types'
import format from 'date-fns/format'

import { Role, UserApps } from '../common/utils/types'

type Pluralize = (number: number, singular: string, plural: string, plural5?: string) => string

export const pluralize: Pluralize = (number, singular, plural, plural5 = plural) => {
  plural5 = plural5 ?? plural

  const lastDigit = number % 10
  const tens = number % 100

  if (tens >= 11 && tens <= 14) {
    return plural5
  } else if (lastDigit === 0) {
    return plural5
  } else if (lastDigit === 1) {
    return singular
  } else if (lastDigit <= 4) {
    return plural
  } else {
    return plural5
  }
}

export const getFileArray = (fileList: FileList): File[] => {
  const list = []
  for (let i = 0; i < fileList.length; i++) {
    list.push(fileList[i])
  }
  return list
}

export const getCompanyAge = (creationDate: string): string => {
  const startDate = new Date(creationDate)
  const endDate = new Date()

  let years = endDate.getFullYear() - startDate.getFullYear()
  let months = endDate.getMonth() - startDate.getMonth()

  if (months < 0) {
    years -= 1
    months += 12
  }

  if (years > 2 && !months) {
    return `${years} godin${pluralize(years, 'a', 'e', 'a')}`
  } else if (years > 2) {
    return `${years} godin${pluralize(years, 'a', 'e', 'a')} i ${months} mesec${pluralize(
      months,
      '',
      'a',
      'i',
    )}`
  } else {
    const totalMonths = years * 12 + months
    return `${totalMonths} mesec${pluralize(months, '', 'a', 'i')}`
  }
}

export const getDateRetryVerification = (months?: string): string => {
  const currentDate = new Date()
  currentDate.setMonth(currentDate.getMonth() + Number(months))

  return currentDate.toISOString()
}

export function formatDate(dateString: Date): string {
  const date = new Date(dateString)
  return format(date, 'dd.MM.yyyy')
}

export const getProposalStatus = (
  tenderStatus: TenderStatus,
  proposalStatus: ProposalStatus,
  deadlineProposal?: Date,
): { status: string; className: string } => {
  const currentDate = new Date()

  if (tenderStatus === TenderStatus.canceled) {
    return { status: StatusInfoName.CANCELLED, className: 'tender-canceled' }
  }

  if (
    proposalStatus === ProposalStatus.DRAFT &&
    deadlineProposal &&
    new Date(deadlineProposal) < currentDate
  ) {
    return { status: StatusInfoName.DEADLINE_PASSED, className: 'deadline-passed' }
  }

  if (tenderStatus === TenderStatus.submitted && proposalStatus === ProposalStatus.DRAFT) {
    return { status: StatusInfoName.DRAFT, className: 'proposal-in-progress' }
  }

  if (
    tenderStatus === TenderStatus.submitted &&
    proposalStatus === ProposalStatus.SUBMITTED &&
    deadlineProposal &&
    new Date(deadlineProposal) > currentDate
  ) {
    return { status: StatusInfoName.PROPOSAL_SENT, className: 'proposal-submitted' }
  }

  if (
    tenderStatus === TenderStatus.submitted &&
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    ((deadlineProposal && new Date(deadlineProposal) < currentDate) ||
      proposalStatus === ProposalStatus.NOT_SELECTED ||
      proposalStatus === ProposalStatus.SELECTED)
  ) {
    return { status: StatusInfoName.CHOOSING_PROPOSALS, className: 'choosing-proposals' }
  }

  if (tenderStatus === TenderStatus.finished && proposalStatus === ProposalStatus.SELECTION_CONFIRMED) {
    return { status: StatusInfoName.PROPOSAL_ACCEPTED, className: 'proposal-accepted' }
  }

  if (
    (tenderStatus === TenderStatus.finished || tenderStatus === TenderStatus.finishedNothingSelected) &&
    proposalStatus === ProposalStatus.NOT_SELECTED
  ) {
    return { status: StatusInfoName.PROPOSAL_NOT_ACCEPTED, className: 'proposal-not-accepted' }
  }

  return { status: '-', className: '-' }
}

export const getMainAppForRole = (role: Role): string => {
  switch (role) {
    case Role.admin:
    case Role.consultant:
      return UserApps.Processing
    case Role.company:
      return UserApps.Company
    case Role.stakeholder:
      return UserApps.Reporting
    case Role.vendor:
      return UserApps.Vendors
    default:
      return UserApps.Company
  }
}
